// src/App.js
import React, {useState, useEffect, useCallback} from "react";
import "./index.css";
import rawPhotos from "./data/photos_with_large.json";
import analytics from "./data/analytics.json";
import { trackGoogleAnalyticsPageView } from "./utils/initGoogleAnalytics";
import {createClient} from "@supabase/supabase-js";
import { Helmet } from "react-helmet";
import Header from "./components/Header";
// import {
//   FacebookShareButton,
//   TwitterShareButton,
//   WhatsappShareButton,
//   FacebookIcon,
//   TwitterIcon,
//   WhatsappIcon,
// } from "react-share";

// Create Supabase client (reads URL and KEY from env vars)
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

function App() {
  // Set page title
  useEffect(() => {
    document.title = "Victor Ursu - a digital medium.";
  }, []);

  const [currentIndex, setCurrentIndex] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [showTempHeart, setShowTempHeart] = useState(false);
  const [likeCounts, setLikeCounts] = useState({});
  const [showTitles, setShowTitles] = useState(true);
  const toggleTitles = () => setShowTitles((prev) => !prev);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const toggleTheme = () => setIsDarkMode((prev) => !prev);
  const [photos, setPhotos] = useState([]);
  const totalLikes = Object.values(likeCounts).reduce((sum, count) => sum + count, 0);

  const [sortById, setSortById] = useState(false);
  const toggleSortById = () => setSortById((prev) => !prev);

  // Ensure we have a persistent browserUuid stored in localStorage
  const [browserUuid, setBrowserUuid] = useState(null);

  useEffect(() => {
    const updated = sortById
      ? [...rawPhotos].sort((a, b) => b.id - a.id)
      : shuffleArray(rawPhotos);
    setPhotos(updated);
  }, [sortById]);

  useEffect(() => {
    let uuid = localStorage.getItem("browserUuid");
    if (!uuid) {
      // modern browsers support crypto.randomUUID()
      uuid = crypto.randomUUID();
      localStorage.setItem("browserUuid", uuid);
    }
    setBrowserUuid(uuid);
  }, []);

  // On mount: fetch all rows from "photoLikes" and build a count map
  useEffect(() => {
    const cacheKey = "photoLikesCache";
    const cacheTTL = 1000 * 60 * 5; // 5 minutes
    const now = Date.now();

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (now - timestamp < cacheTTL) {
        setLikeCounts(data);
        return; // ✅ Skip fetch
      }
    }

    async function fetchAllLikeCounts() {
      const { data: allLikes, error } = await supabase
        .from("photoLikes")
        .select("photoId");

      if (error) {
        console.error("Error fetching like counts:", error);
        return;
      }

      const counts = {};
      allLikes.forEach(({ photoId }) => {
        counts[photoId] = (counts[photoId] || 0) + 1;
      });

      setLikeCounts(counts);
      localStorage.setItem(cacheKey, JSON.stringify({ data: counts, timestamp: now }));
    }

    fetchAllLikeCounts();
  }, []);


  // Open lightbox at given index and update URL to "/<slug>"
  const openLightbox = useCallback(
    (index) => {
      if (photos[index].large || photos[index].video) {
        setIsLoading(true);
        setCurrentIndex(index);

        const likedKey = `liked_${photos[index].id}`;
        setIsLiked(localStorage.getItem(likedKey) === "true");

        // ✅ Set page title
        document.title = (photos[index].alt ? photos[index].alt + " by Victor Ursu" : "Victor Ursu");

        // Update URL to "/<slug>" and record a GA4 page view (gallery click, next/prev, keyboard)
        const slug = photos[index].slug;
        if (slug) {
          const pagePath = `/${slug}`;
          window.history.replaceState(null, "", pagePath);
          trackGoogleAnalyticsPageView(analytics.gaMeasurementId, {
            pagePath,
            pageTitle: document.title,
          });
        }
      }
    },
    [photos]
  );

  // On mount, check if the pathname matches a slug → open that image
  useEffect(() => {
    const path = window.location.pathname.replace(/^\//, ""); // strip leading “/”
    if (path === "grand-canyon-packrafting") return;
    if (!path) return; // if root path, do nothing
    const idx = photos.findIndex((m) => m.slug === path);
    if (idx !== -1) {
      // Delay to ensure React has mounted
      setTimeout(() => {
        openLightbox(idx);
      }, 0);
    }
  }, [openLightbox, photos]);

  // Close lightbox and revert URL back to "/"
  const closeLightbox = useCallback(() => {
    setCurrentIndex(null);
    setIsLoading(true);
    setIsLiked(false);

    // ✅ Reset title
    document.title = "Victor Ursu - a digital medium.";

    // Return to the base URL when closing
    window.history.replaceState(null, "", "/");
    trackGoogleAnalyticsPageView(analytics.gaMeasurementId, {
      pagePath: "/",
      pageTitle: document.title,
    });
  }, []);

  // “Like” only when clicking the media (image or video)
  const handleMediaClick = async (e) => {
    e.stopPropagation();
    if (currentIndex === null || !browserUuid) return;
    const photoId = photos[currentIndex].id;
    const key = `liked_${photoId}`;

    if (!isLiked) {
      // 1) Update localStorage and UI
      localStorage.setItem(key, "true");
      setIsLiked(true);
      setShowTempHeart(true);
      setTimeout(() => setShowTempHeart(false), 800);

      // 2) ALSO record this like in Supabase.photoLikes
      const {data, error} = await supabase
        .from("photoLikes")
        .insert([{photoId, browserUuid}]);

      if (error) {
        console.error("Failed to record like in Supabase (media click):", error);
      } else {
        console.log("Recorded like in Supabase (media click):", data);

        // 3) Update likeCounts state for this photo
        setLikeCounts((prev) => ({
          ...prev,
          [photoId]: (prev[photoId] || 0) + 1,
        }));
      }
    }
  };

  // Toggle “like”/“unlike” when clicking the heart icon
  const toggleLike = async (e) => {
    e.stopPropagation();
    if (currentIndex === null || !browserUuid) return;
    const photoId = photos[currentIndex].id;
    const key = `liked_${photoId}`;

    if (isLiked) {
      // User is un‐hearting: remove from localStorage only
      localStorage.removeItem(key);
      setIsLiked(false);
      // ALSO delete the like from Supabase
      const {data, error} = await supabase
        .from("photoLikes")
        .delete()
        .eq("photoId", photoId)
        .eq("browserUuid", browserUuid);

      if (error) {
        console.error("Failed to remove like from Supabase:", error);
      } else {
        console.log("Removed like from Supabase:", data);
        // 3) Decrement our local count (minimum 0)
        setLikeCounts((prev) => ({
          ...prev,
          [photoId]: Math.max((prev[photoId] || 1) - 1, 0),
        }));
      }
    } else {
      // Mark as liked locally (so heart icon updates immediately)
      localStorage.setItem(key, "true");
      setIsLiked(true);
      setShowTempHeart(true);

      setTimeout(() => setShowTempHeart(false), 800);

      // Insert a new row into Supabase.photoLikes
      const {data, error} = await supabase
        .from("photoLikes")
        .insert([{photoId, browserUuid}]);

      if (error) {
        console.error("Failed to record like in Supabase:", error);
        // Optionally: you could roll back localStorage/state here if needed
      } else {
        console.log("Recorded like in Supabase:", data);
        setLikeCounts((prev) => ({
          ...prev,
          [photoId]: (prev[photoId] || 0) + 1,
        }));
      }
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (currentIndex === null) return;
      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        const prevIndex = (currentIndex - 1 + photos.length) % photos.length;
        openLightbox(prevIndex);
      } else if (e.key === "ArrowRight") {
        const nextIndex = (currentIndex + 1) % photos.length;
        openLightbox(nextIndex);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, closeLightbox, openLightbox, photos.length]);



  return (
     <div
       style={{
         backgroundColor: isDarkMode ? "#000" : "#e2e2e2",
         backgroundImage: isDarkMode ? "url('./assets/orion.jpg')" : "url('./assets/shells.jpg')",
         backgroundSize: "cover",
         backgroundPosition: "center",
         backgroundRepeat: "no-repeat",
         backgroundAttachment: "fixed",
         minHeight: "100vh",
       }}
      >
       {currentIndex !== null && (
         <Helmet>
           <meta property="og:title" content={photos[currentIndex].alt || "Victor Ursu"} />
           <meta property="og:image" content={photos[currentIndex].large} />
           <meta property="og:type" content="website" />
           <meta property="og:url" content={window.location.href} />
         </Helmet>
       )}

       <Header
         showTitles={showTitles}
         toggleTitles={toggleTitles}
         isDarkMode={isDarkMode}
         toggleTheme={toggleTheme}
         totalLikes={totalLikes}
         sortById={sortById}
         toggleSortById={toggleSortById}
       />
      {/* ================= Masonry Gallery ================= */}
      <div className="gallery-container">
        {photos.map((media, index) => {
          const listingLiked =
            localStorage.getItem(`liked_${media.id}`) === "true";

          return (
            <div
              key={media.id}
              className="photo-item"
              onClick={() => openLightbox(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  openLightbox(index);
                }
              }}
            >
              <img
                src={media.src}
                alt={media.alt || `Media ${media.id}`}
                loading="lazy"
                width={media.width}
                height={media.height}
                className="gallery-img"
                style={{
                  border: `1px solid ${isDarkMode ? "#fff" : "#000"}`,
                  borderRadius: "4px",
                }}
              />
              {showTitles && media.alt && (
                <div className="photo-overlay">{media.alt}</div>
              )}

              {/* PLAY ICON OVERLAY FOR VIDEO */}
              {media.video && (
                <div className="video-play-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 64 64"
                    width="24"
                    height="24"
                    fill="#fff"
                  >
                    <polygon points="24,16 52,32 24,48"/>
                  </svg>
                </div>
              )}

              {/* Static heart icon on listing */}
              {showTitles && (
              <div className="photo-heart-icon">
                {listingLiked ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="10"
                    height="10"
                    fill="#e74c3c"
                    stroke="#e74c3c"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                  2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09
                  C13.09 3.81 14.76 3 16.5 3
                  19.58 3 22 5.42 22 8.5
                  c0 3.78-3.4 6.86-8.55 11.54
                  L12 21.35z"/>
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="10"
                    height="10"
                    fill="none"
                    stroke="#e74c3c"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                  2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09
                  C13.09 3.81 14.76 3 16.5 3
                  19.58 3 22 5.42 22 8.5
                  c0 3.78-3.4 6.86-8.55 11.54
                  L12 21.35z"/>
                  </svg>
                )}
              </div>
              )}
              {/* Dynamic count from Supabase */}
              {showTitles && (
              <div className="photo-heart-count">
                {likeCounts[media.id] || ""}
              </div>
              )}

              {/* Magnifier icon */}
              {showTitles && media.large && (
                <div className="magnifier-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="10"
                    height="10"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    <line x1="11" y1="8" x2="11" y2="14"/>
                    <line x1="8" y1="11" x2="14" y2="11"/>
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ================= Lightbox Modal ================= */}
      {currentIndex !== null && (
        <div className="lightbox-overlay" style={{ zIndex: 1100 }}  onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {/* Close “×” */}
            <button
              className="lightbox-close"
              onClick={closeLightbox}
              aria-label="Close"
            >
              &times;
            </button>

            {/* Prev arrow */}
            <button
              className="lightbox-nav lightbox-nav-left"
              onClick={(e) => {
                e.stopPropagation();
                const prevIndex =
                  (currentIndex - 1 + photos.length) % photos.length;
                openLightbox(prevIndex);
              }}
              aria-label="Previous"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6"/>
              </svg>
            </button>

            {/* Next arrow */}
            <button
              className="lightbox-nav lightbox-nav-right"
              onClick={(e) => {
                e.stopPropagation();
                const nextIndex = (currentIndex + 1) % photos.length;
                openLightbox(nextIndex);
              }}
              aria-label="Next"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                fill="none"
                stroke="#fff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </button>

            {/* Top-left icons: heart + copy URL */}
            <div className="lightbox-top-left-icons">
              <div className="lightbox-heart" onClick={toggleLike}>
              {isLiked ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  fill="#e74c3c"
                  stroke="none"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                  2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09
                  C13.09 3.81 14.76 3 16.5 3
                  19.58 3 22 5.42 22 8.5
                  c0 3.78-3.4 6.86-8.55 11.54
                  L12 21.35z"/>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  fill="none"
                  stroke="#e74c3c"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                  2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09
                  C13.09 3.81 14.76 3 16.5 3
                  19.58 3 22 5.42 22 8.5
                  c0 3.78-3.4 6.86-8.55 11.54
                  L12 21.35z"/>
                </svg>
              )}
              </div>
              <button
                className="lightbox-copy-url"
                onClick={(e) => {
                  e.stopPropagation();
                  navigator.clipboard.writeText(window.location.href);
                }}
                aria-label="Copy image URL"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              </button>
            </div>

            {/* Temporary heart overlay (fades out) */}
            {showTempHeart && (
              <div className="temp-heart-overlay">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="180"
                  height="180"
                  fill="#e74c3c"
                  stroke="none"
                >
                  <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5
                  2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09
                  C13.09 3.81 14.76 3 16.5 3
                  19.58 3 22 5.42 22 8.5
                  c0 3.78-3.4 6.86-8.55 11.54
                  L12 21.35z"/>
                </svg>
              </div>
            )}

            {/* Spinner while loading */}
            {isLoading && (
              <div className="lightbox-spinner-container">
                <div className="lightbox-spinner"/>
              </div>
            )}

            {/* Conditionally render video or image */}
            {photos[currentIndex].video ? (
              // Embed YouTube if URL includes “youtube.com” or “youtu.be”
              /youtu\.be|youtube\.com/.test(photos[currentIndex].video) ? (
                <iframe
                  className="lightbox-video"
                  src={
                    photos[currentIndex].video.includes("watch?v=")
                      ? photos[currentIndex].video.replace(
                        /watch\?v=([^&]+)/,
                        "embed/$1"
                      )
                      : photos[currentIndex].video.includes("youtu.be/")
                        ? photos[currentIndex].video.replace(
                          /youtu\.be\/([^?]+)/,
                          "youtube.com/embed/$1"
                        )
                        : photos[currentIndex].video
                  }
                  frameBorder="0"
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="YouTube Video"
                  onLoad={() => setIsLoading(false)}
                  style={{
                    display: isLoading ? "none" : "block",
                    width: "120vh",
                    height: "90vh",
                  }}
                  onClick={handleMediaClick}
                />
              ) : (
                <video
                  src={photos[currentIndex].video}
                  className="lightbox-video"
                  controls
                  autoPlay
                  onLoadedData={() => setIsLoading(false)}
                  style={{
                    display: isLoading ? "none" : "block",
                    cursor: "pointer",
                  }}
                  onClick={handleMediaClick}
                />
              )
            ) : (
              <img
                src={photos[currentIndex].large}
                alt={photos[currentIndex].alt || ""}
                className="lightbox-img"
                onLoad={() => setIsLoading(false)}
                style={{
                  display: isLoading ? "none" : "block",
                  cursor: "pointer",
                }}
                onClick={handleMediaClick}
              />
            )}

            {/* Caption */}
            {!isLoading && photos[currentIndex].alt && (
              <div className="lightbox-caption">
                {photos[currentIndex].alt}
              </div>
            )}

            {/* Download and Share buttons */}
            {/*{!isLoading && photos[currentIndex].large && (*/}
            {/*  <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>*/}
            {/*    <FacebookShareButton url={window.location.href}>*/}
            {/*      <FacebookIcon size={32} round />*/}
            {/*    </FacebookShareButton>*/}
            {/*    <TwitterShareButton url={window.location.href}>*/}
            {/*      <TwitterIcon size={32} round />*/}
            {/*    </TwitterShareButton>*/}
            {/*    <WhatsappShareButton url={window.location.href}>*/}
            {/*      <WhatsappIcon size={32} round />*/}
            {/*    </WhatsappShareButton>*/}
            {/*  </div>*/}
            {/*)}*/}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
